/**
 * Advertising Pricing Mirror (Task 5)
 * Matches the logic in app/Services/Advertising/AdvertisingPricingService.php
 */

export function quoteSlot(pageKey, placementKey, device, locale, durationKey, blockIndex = 1, config) {
    const pricing = config.pricing || {};
    const pageKeyMultipliers = config.page_key_multipliers || {};
    const avgPagesMap = config.avg_pages || {};
    const volumeDiscounts = config.volume_discounts || {};
    const durations = config.durations || [];

    const duration = durations.find(d => d.key === durationKey) || durations.find(d => d.key === 'week') || { days: 7, multiplier: 1 };

    const monthlyVisitors = Math.max(parseInt(pricing.estimated_monthly_visitors || 10000, 10), 1);
    const directCpmUah = Math.max(parseFloat(pricing.direct_cpm_uah || 300), 1);
    const baseWeek = (monthlyVisitors / 1000) * directCpmUah * (7 / 30);

    let pageMultiplier = 1.0;
    if (pageKeyMultipliers[pageKey] !== undefined) {
        pageMultiplier = parseFloat(pageKeyMultipliers[pageKey]);
    } else if (pricing.page_multipliers && pricing.page_multipliers[pageKey] !== undefined) {
        pageMultiplier = parseFloat(pricing.page_multipliers[pageKey]);
    }

    const placementMultiplier = parseFloat(pricing.placement_multipliers?.[placementKey] ?? 1.0);
    const deviceMultiplier = parseFloat(pricing.device_multipliers?.[device] ?? 1.0);
    const languageMultiplier = 1.0; // removed — all ads target all locales ('*')
    const durationMultiplier = parseFloat(duration.multiplier);

    // Coverage multiplier removed — page_key_multipliers already encodes each page's value relative to home
    const coverageMultiplier = 1.0;

    let discountFraction = 0.0;
    const sortedThresholds = Object.keys(volumeDiscounts)
        .map(Number)
        .sort((a, b) => b - a);

    for (const minBlocks of sortedThresholds) {
        if (blockIndex >= minBlocks) {
            discountFraction = parseFloat(volumeDiscounts[minBlocks]);
            break;
        }
    }

    const priceBeforeDiscount = baseWeek
        * pageMultiplier
        * placementMultiplier
        * deviceMultiplier
        * durationMultiplier;

    const rawAmount = priceBeforeDiscount * (1 - discountFraction);
    const amount = Math.round(rawAmount / 5.0) * 5;

    const eurRate = Math.max(parseFloat(pricing.eur_rate || 45), 1);
    const amountEur = parseFloat((amount / eurRate).toFixed(2));

    return {
        amount_uah: amount,
        amount_minor: amount * 100,
        amount_eur: amountEur,
        currency: pricing.currency || 'UAH',
        page_key: pageKey,
        placement_key: placementKey,
        device_target: device,
        locale_target: locale,
        duration_key: durationKey,
        duration_days: duration.days,
        price_uah: amount,
        price_eur: amountEur,
        base_week_uah: Math.round(baseWeek),
        breakdown: {
            base: parseFloat(baseWeek.toFixed(2)),
            page_mult: parseFloat(pageMultiplier.toFixed(4)),
            placement_mult: parseFloat(placementMultiplier.toFixed(4)),
            device_mult: parseFloat(deviceMultiplier.toFixed(4)),
            language_mult: 1.0,
            duration_mult: parseFloat(durationMultiplier.toFixed(4)),
            coverage_mult: 1.0,
            volume_discount: parseFloat(discountFraction.toFixed(4)),
        }
    };
}

export function quoteBlocks(blocks, config) {
    return blocks.map((block, i) => {
        return quoteSlot(
            block.page_key || block.pageKey || 'home',
            block.placement_key || block.placementKey || 'top',
            block.device_target || block.device || 'mobile',
            block.locale_target || block.locale || '*',
            block.duration_key || block.durationKey || 'week',
            i + 1,
            config
        );
    });
}

if (typeof window !== 'undefined') {
    window.AdvertisingPricing = { quoteSlot, quoteBlocks };
}
